/**
 * The loading placeholder every list and panel waits behind.
 *
 * This shape was hand-rolled in more than twenty files as a bare
 * `animate-pulse` div, which meant two things nobody intended: a screen reader
 * announced nothing while the page was busy, and the pulse kept animating for
 * people who have asked their system to stop animations. Both are fixed here,
 * once.
 */
export function Skeleton({
  className = '',
  radius = 'lg',
}: {
  className?: string;
  /** Match the radius of whatever is loading, so nothing jumps on arrival. */
  radius?: 'md' | 'lg' | 'xl' | 'full';
}) {
  const radii = {
    md: 'rounded-lg',
    lg: 'rounded-2xl',
    xl: 'rounded-3xl',
    full: 'rounded-full',
  } as const;

  return (
    <div
      aria-hidden="true"
      className={`bg-slate-100 motion-safe:animate-pulse ${radii[radius]} ${className}`}
    />
  );
}

/**
 * A group of placeholders that announces itself as busy.
 *
 * Wrap the skeletons for one region in this rather than labelling each one:
 * "loading" is a fact about the region, and a screen reader should hear it
 * once, not once per grey rectangle.
 */
export function SkeletonGroup({
  label = 'Loading',
  className = '',
  children,
}: {
  label?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div role="status" aria-busy="true" aria-live="polite" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** The common case: n stacked rows of equal height. */
export function SkeletonRows({
  rows = 3,
  height = 'h-20',
  label,
}: {
  rows?: number;
  height?: string;
  label?: string;
}) {
  return (
    <SkeletonGroup label={label} className="space-y-3">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className={height} />
      ))}
    </SkeletonGroup>
  );
}
